import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Building2, 
  Plus, 
  MapPin, 
  Edit2,
  Trash2,
  Save,
  X,
  Phone,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import RatingsUpdateDialog from '../components/RatingsUpdateDialog';
import { BRAND_PHOTOS, storePhoto } from '../lib/brand';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stores() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [ratingsOpen, setRatingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingStore) {
        // Update existing store
        await axios.put(`${API_URL}/stores/${editingStore.id}`, formData);
        setStores(stores.map(s => s.id === editingStore.id ? { ...s, ...formData } : s));
        toast.success('Store updated successfully');
      } else {
        // Create new store
        const response = await axios.post(`${API_URL}/stores`, formData);
        setStores([...stores, response.data]);
        toast.success('Store created successfully');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving store:', error);
      toast.error(error.response?.data?.detail || 'Failed to save store');
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name,
      address: store.address || '',
      city: store.city || '',
      state: store.state || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('Are you sure you want to delete this store? This will also delete all associated data.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/stores/${storeId}`);
      setStores(stores.filter(s => s.id !== storeId));
      toast.success('Store deleted successfully');
    } catch (error) {
      console.error('Error deleting store:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete store');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStore(null);
    setFormData({ name: '', address: '', city: '', state: '' });
  };

  if (user?.role !== 'owner') {
    return (
      <div className="text-center py-12" data-testid="no-access">
        <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Access Restricted
        </h2>
        <p className="text-slate-600">Only owners can manage stores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stores-page">
      {/* Banner — Elmwood restaurant photo */}
      <div className="relative min-h-[220px] sm:min-h-[260px] rounded-2xl overflow-hidden shadow-md grain" data-testid="stores-banner">
        <img src={BRAND_PHOTOS.angle} alt="Elmwood A&W restaurant exterior" className="absolute inset-0 w-full h-full object-cover object-[center_55%] animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3E2418]/95 via-[#3E2418]/65 to-[#3E2418]/15" />
        <div className="relative z-10 h-full min-h-[220px] sm:min-h-[260px] flex flex-col justify-end p-6 sm:p-8 text-white reveal">
          <p className="text-[11px] uppercase tracking-[0.3em] text-orange-300 font-semibold">Elmwood Management</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Store Network</h1>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="glass-dark rounded-full px-3 py-1 text-xs font-semibold" data-testid="stores-count-badge">{stores.length} restaurants</span>
            <span className="glass-dark rounded-full px-3 py-1 text-xs font-semibold">New Brunswick &amp; Newfoundland</span>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-[#3E2418] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Store Locations
          </h2>
          <p className="text-slate-600 text-sm mt-0.5">
            Manage your franchise store locations
          </p>
        </div>
        
        <div className="flex gap-2">
          {user?.role === 'owner' && (
            <Button variant="outline" onClick={() => setRatingsOpen(true)} className="rounded-full border-slate-200 btn-press" data-testid="update-ratings-btn">
              <Star className="w-4 h-4 mr-2 text-amber-400 fill-amber-400" />
              Update Google ratings
            </Button>
          )}
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#F26522] hover:bg-[#d9551a] text-white rounded-full shadow-md shadow-orange-500/20 btn-press"
            data-testid="add-store-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Store
          </Button>
        </div>
        <RatingsUpdateDialog open={ratingsOpen} onOpenChange={setRatingsOpen} stores={stores} onSaved={fetchStores} />
      </div>

      {/* Add/Edit Store Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editingStore ? 'Edit Store' : 'Add New Store'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Store Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., McAllister Place"
                className="rounded-sm"
                required
                data-testid="store-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street"
                className="rounded-sm"
                data-testid="store-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Saint John"
                  className="rounded-sm"
                  data-testid="store-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">Province/State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="NB"
                  className="rounded-sm"
                  data-testid="store-state-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                className="rounded-sm"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
                data-testid="save-store-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingStore ? 'Update Store' : 'Add Store'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stores Grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-500" data-testid="loading">
          Loading stores...
        </div>
      ) : stores.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="empty-state">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              No stores yet
            </h3>
            <p className="text-slate-600 mb-6">
              Add your first store location to get started
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Store
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger" data-testid="stores-grid">
          {stores.map((store, index) => {
            const comingSoon = (store.status || store.state) === 'coming_soon';
            const rating = store.google_rating ?? store.rating;
            return (
            <Card
              key={store.id}
              className="group bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
              data-testid={`store-${store.id}`}
            >
              <div className="relative h-44 overflow-hidden bg-slate-100">
                <img src={storePhoto(index)} alt={`${store.name} restaurant`} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-testid={`store-photo-${store.id}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3E2418]/80 via-[#3E2418]/10 to-transparent" />
                <span className={`absolute top-3 left-3 text-[10px] px-2.5 py-1 rounded-full font-bold tracking-wide backdrop-blur-md ${comingSoon ? 'bg-amber-400/90 text-amber-950' : 'bg-white/90 text-emerald-700'}`} data-testid={`store-status-${store.id}`}>
                  {comingSoon ? 'COMING SOON' : 'ACTIVE'}
                </span>
                {rating && (
                  <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-full text-xs font-bold text-slate-800 shadow-sm flex items-center gap-1" data-testid={`store-rating-${store.id}`}>
                    <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />{rating.toFixed(1)}
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <h3 className="font-bold text-lg leading-tight drop-shadow" style={{ fontFamily: 'Chivo, sans-serif' }}>{store.name}</h3>
                  {(store.city || store.province) && (
                    <p className="text-xs text-white/80 mt-0.5">
                      {store.city}{store.province ? `${store.city ? ', ' : ''}${store.province}` : ''}
                    </p>
                  )}
                </div>
              </div>
              <CardContent className="p-5 flex-1 flex flex-col">
                <div className="space-y-2 flex-1">
                  {store.address && (
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 mt-0.5 text-[#F26522] flex-shrink-0" />
                      <span>{store.address}</span>
                    </div>
                  )}
                  {store.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-[#F26522] flex-shrink-0" />
                      <a href={`tel:${store.phone}`} className="hover:text-[#F26522] transition-colors">{store.phone}</a>
                    </div>
                  )}
                  {rating && (
                    <p className="text-xs text-slate-400">Google rating{store.rating_updated_at ? ` · w/e ${store.rating_updated_at}` : ''}</p>
                  )}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-400">Added {store.created_at ? new Date(store.created_at).toLocaleDateString() : 'recently'}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(store)}
                      className="text-slate-500 hover:text-[#F26522] hover:bg-orange-50 rounded-full"
                      data-testid={`edit-store-${store.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(store.id)}
                      className="text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-full"
                      data-testid={`delete-store-${store.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
